(() => {
  if (window.__productCardCustomBadgesInitialized === true) {
    return;
  }

  window.__productCardCustomBadgesInitialized = true;

  const ROOT_SELECTOR = '[data-product-card-badges]';
  const BADGE_SELECTOR = '[data-product-card-badge]';

  /**
   * @param {unknown} value
   * @returns {value is HTMLElement}
   */
  function isHTMLElement(value) {
    return value instanceof HTMLElement;
  }

  /**
   * @param {HTMLElement} element
   * @param {string} key
   * @returns {number}
   */
  function getDatasetNumber(element, key) {
    const value = Number(element.dataset[key] || 0);

    return Number.isFinite(value) ? value : 0;
  }

  /**
   * @param {HTMLElement} badge
   * @param {number} currentTimestamp
   * @returns {{
   *   isActive: boolean,
   *   nextTransitionTimestamp: number | null
   * }}
   */
  function getBadgeTimeState(badge, currentTimestamp) {
    const startTimestamp = getDatasetNumber(badge, 'startTs');
    const endTimestamp = getDatasetNumber(badge, 'endTs');

    const isBeforeStart =
      startTimestamp > 0 &&
      currentTimestamp < startTimestamp;

    const isExpired =
      endTimestamp > 0 &&
      currentTimestamp >= endTimestamp;

    let nextTransitionTimestamp = null;

    if (isBeforeStart) {
      nextTransitionTimestamp = startTimestamp;
    } else if (!isExpired && endTimestamp > currentTimestamp) {
      nextTransitionTimestamp = endTimestamp;
    }

    return {
      isActive: !isBeforeStart && !isExpired,
      nextTransitionTimestamp,
    };
  }

  /**
   * Un número menor representa una prioridad mayor.
   *
   * @param {HTMLElement | null} currentBadge
   * @param {HTMLElement} candidateBadge
   * @returns {HTMLElement}
   */
  function getHigherPriorityBadge(currentBadge, candidateBadge) {
    if (!currentBadge) {
      return candidateBadge;
    }

    const currentPriority = getDatasetNumber(currentBadge, 'priority');
    const candidatePriority = getDatasetNumber(candidateBadge, 'priority');

    return candidatePriority < currentPriority
      ? candidateBadge
      : currentBadge;
  }

  /**
   * @param {HTMLElement} badge
   * @returns {void}
   */
  function showBadge(badge) {
    badge.hidden = false;
    badge.setAttribute('aria-hidden', 'false');
  }

  /**
   * @param {HTMLElement} badge
   * @returns {void}
   */
  function hideBadge(badge) {
    badge.hidden = true;
    badge.setAttribute('aria-hidden', 'true');
  }

  /**
   * @param {HTMLElement} root
   * @returns {number | null}
   */
  function synchronizeBadges(root) {
    const currentTimestamp = Math.floor(Date.now() / 1000);

    const badges = Array.from(
      root.querySelectorAll(BADGE_SELECTOR)
    ).filter(isHTMLElement);

    const activeBadges = [];
    let nextTransitionTimestamp = null;

    badges.forEach((badge) => {
      const state = getBadgeTimeState(badge, currentTimestamp);

      if (state.isActive) {
        activeBadges.push(badge);
      }

      if (state.nextTransitionTimestamp !== null) {
        nextTransitionTimestamp =
          nextTransitionTimestamp === null
            ? state.nextTransitionTimestamp
            : Math.min(nextTransitionTimestamp, state.nextTransitionTimestamp);
      }
    });

    const selectedBadges = [];

    const activeImageBadges = activeBadges.filter(
      (badge) => badge.dataset.hasImage === 'true'
    );

    if (activeImageBadges.length > 0) {
      const selectedImageBadge = activeImageBadges.reduce(
        (currentBadge, candidateBadge) =>
          getHigherPriorityBadge(currentBadge, candidateBadge),
        null
      );

      if (selectedImageBadge) {
        selectedBadges.push(selectedImageBadge);
      }
    } else {
      const selectedByPosition = {
        left: null,
        center: null,
        right: null,
      };

      activeBadges.forEach((badge) => {
        const position = badge.dataset.position || 'left';

        if (
          !Object.prototype.hasOwnProperty.call(
            selectedByPosition,
            position
          )
        ) {
          return;
        }

        selectedByPosition[position] = getHigherPriorityBadge(
          selectedByPosition[position],
          badge
        );
      });

      Object.values(selectedByPosition).forEach((badge) => {
        if (badge) {
          selectedBadges.push(badge);
        }
      });
    }

    const selectedBadgesSet = new Set(selectedBadges);

    badges.forEach((badge) => {
      if (selectedBadgesSet.has(badge)) {
        showBadge(badge);
      } else {
        hideBadge(badge);
      }
    });

    const hasVisibleBadges = selectedBadges.length > 0;

    root.hidden = !hasVisibleBadges;

    root.setAttribute(
      'aria-hidden',
      hasVisibleBadges ? 'false' : 'true'
    );

    return nextTransitionTimestamp;
  }

  /**
   * @param {HTMLElement} root
   * @returns {void}
   */
  function initializeRoot(root) {
    if (root.dataset.productCardBadgesInitialized === 'true') {
      return;
    }

    root.dataset.productCardBadgesInitialized = 'true';

    let timeoutId = null;

    const update = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }

      const nextTransitionTimestamp = synchronizeBadges(root);

      if (nextTransitionTimestamp === null) {
        return;
      }

      const currentTimestamp = Math.floor(Date.now() / 1000);

      const delay = Math.max(
        (nextTransitionTimestamp - currentTimestamp) * 1000 + 100,
        1000
      );

      timeoutId = window.setTimeout(update, delay);
    };

    update();
  }

  /**
   * @param {ParentNode} scope
   * @returns {void}
   */
  function initializeBadges(scope = document) {
    if (
      scope instanceof HTMLElement &&
      scope.matches(ROOT_SELECTOR)
    ) {
      initializeRoot(scope);
    }

    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!isHTMLElement(root)) {
        return;
      }

      initializeRoot(root);
    });
  }

  initializeBadges();

  document.addEventListener('DOMContentLoaded', () => {
    initializeBadges();
  });

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    initializeBadges(target);
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        initializeBadges(node);
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();